import React, { useEffect, useRef, useState } from "react";
import { AppLayout } from "src/components/layouts";
import AdminService from "src/services/admin.services";
import { Restaurant } from "../../services/types";
import SearchInput from "../../components/ui/SearchInput";
import removeRedEye from "../../assets/icons/removeRedEye.svg";
import BackButton from "src/components/BackButton";
import FoodLoader from "src/components/Loader";
import { QueryFunctionContext, useQuery } from "react-query";
import { format } from "date-fns";
import { NIGERIAN_STATES } from "../../utils/constants";
import CustomSelect from "../../components/ui/CustomSelect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { saveAs } from "file-saver";

// Nigerian state coordinates (approximate center points)
const getStateFromCoordinates = (
  latitude: number,
  longitude: number
): string => {
  const stateCoordinates: {
    [key: string]: { lat: number; lng: number; radius: number };
  } = {
    Abuja: { lat: 9.082, lng: 7.3986, radius: 50.0 },
    Lagos: { lat: 6.5244, lng: 3.3792, radius: 80.0 },
    Kano: { lat: 11.9914, lng: 8.5311, radius: 60.0 },
    Kaduna: { lat: 10.5222, lng: 7.4384, radius: 50.0 },
    Katsina: { lat: 12.9914, lng: 7.6011, radius: 50.0 },
    Jigawa: { lat: 12.57, lng: 9.56, radius: 50.0 },
    Yobe: { lat: 12.0, lng: 11.5, radius: 50.0 },
    Borno: { lat: 11.8333, lng: 13.15, radius: 50.0 },
    Adamawa: { lat: 9.3273, lng: 12.3984, radius: 50.0 },
    Taraba: { lat: 8.8833, lng: 11.3667, radius: 50.0 },
    Gombe: { lat: 10.2897, lng: 11.1673, radius: 50.0 },
    Bauchi: { lat: 10.3158, lng: 9.8444, radius: 50.0 },
    Plateau: { lat: 9.8965, lng: 8.8583, radius: 50.0 },
    Nasarawa: { lat: 8.5, lng: 8.0, radius: 50.0 },
    Niger: { lat: 9.6, lng: 6.55, radius: 50.0 },
    Kebbi: { lat: 12.45, lng: 4.2, radius: 50.0 },
    Sokoto: { lat: 13.0667, lng: 5.2333, radius: 50.0 },
    Zamfara: { lat: 12.1667, lng: 6.1667, radius: 50.0 },
    Kogi: { lat: 7.8, lng: 6.7333, radius: 50.0 },
    Kwara: { lat: 8.5, lng: 4.55, radius: 50.0 },
    Oyo: { lat: 7.85, lng: 3.9333, radius: 50.0 },
    Osun: { lat: 7.75, lng: 4.5667, radius: 50.0 },
    Ondo: { lat: 7.25, lng: 5.2, radius: 50.0 },
    Ekiti: { lat: 7.6333, lng: 5.2167, radius: 50.0 },
    Ogun: { lat: 7.1667, lng: 3.35, radius: 50.0 },
    Edo: { lat: 6.3176, lng: 5.6145, radius: 50.0 },
    Delta: { lat: 5.5, lng: 5.75, radius: 50.0 },
    Rivers: { lat: 4.75, lng: 7.0, radius: 80.0 },
    Bayelsa: { lat: 4.75, lng: 6.25, radius: 50.0 },
    "Akwa Ibom": { lat: 5.0, lng: 7.85, radius: 50.0 },
    "Cross River": { lat: 5.5, lng: 8.0, radius: 50.0 },
    Ebonyi: { lat: 6.25, lng: 8.1, radius: 50.0 },
    Enugu: { lat: 6.45, lng: 7.5, radius: 50.0 },
    Anambra: { lat: 6.2, lng: 7.0, radius: 50.0 },
    Imo: { lat: 5.5, lng: 7.0, radius: 50.0 },
    Abia: { lat: 5.5, lng: 7.5, radius: 50.0 },
  };

  // Calculate distance between two points using Haversine formula
  const calculateDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Find the closest state
  let closestState = "Unknown";
  let minDistance = Infinity;
  let closestStateWithinRadius = "Unknown";
  let minDistanceWithinRadius = Infinity;

  for (const [state, coords] of Object.entries(stateCoordinates)) {
    const distance = calculateDistance(
      latitude,
      longitude,
      coords.lat,
      coords.lng
    );

    // Always track the closest state regardless of radius
    if (distance < minDistance) {
      minDistance = distance;
      closestState = state;
    }

    // Track the closest state within radius
    if (distance <= coords.radius && distance < minDistanceWithinRadius) {
      minDistanceWithinRadius = distance;
      closestStateWithinRadius = state;
    }
  }

  // Return the state within radius if found, otherwise return the closest state with distance
  if (closestStateWithinRadius !== "Unknown") {
    return closestStateWithinRadius;
  } else {
    return `${closestState} (${minDistance.toFixed(1)}km)`;
  }
};

const adminService = new AdminService();

const fetchRestaurantsQuery = async (ctx: QueryFunctionContext) => {
  const [_key, currentPage, itemsPerPage, searchTerm] = ctx.queryKey;
  const response = await adminService.getRestaurants(
    currentPage as number,
    itemsPerPage as number,
    searchTerm as string
  );

  if (!response || !Array.isArray(response.data)) {
    throw new Error("Invalid response structure");
  }

  return response;
};

const RestaurantsManagement: React.FC = () => {
  const [data, setData] = useState<Restaurant[]>([]);
  const [filteredData, setFilteredData] = useState<Restaurant[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(10);
  // const [isLoading, setIsLoading] = useState<boolean>(true);
  // const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<number>(0); // Manage tabs
  const itemsPerPage = 40;
  const [searching, setSearching] = useState<boolean>(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedState, setSelectedState] = useState<string>("all");
  const [filter, setFilter] = useState<string>("all");

  const {
    data: restaurantsData,
    isLoading,
    isError,
    error,
  } = useQuery(
    ["restaurants", currentPage, itemsPerPage, searchTerm],
    fetchRestaurantsQuery,
    {
      keepPreviousData: true,
      enabled: !!currentPage && !!itemsPerPage,
      refetchInterval: 30000,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        const restaurants = response.data;
        setData(restaurants);
        if (!searching) setFilteredData(restaurants);
        // Calculate total pages based on total items and items per page
        const totalItems = response.total || restaurants.length;
        setTotalPages(Math.ceil(totalItems / itemsPerPage));
      },
      onError: () => {
        // Error fetching restaurants - silently fail
      },
    }
  );

  // useEffect(() => {
  //   fetchData(); // Fetch with the current page number

  //   let interval: NodeJS.Timeout | undefined;

  //   if (!searching) {
  //     interval = setInterval(fetchData, 60000); // 60 seconds
  //   }

  //   return () => {
  //     if (interval) clearInterval(interval);
  //   };
  // }, [currentPage, searching]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      let filtered = [...data];

      // Apply state filter
      if (selectedState !== "all") {
        filtered = filtered.filter((restaurant) => {
          // Use coordinates from restaurant.location.Coordinates if available
          if (restaurant.location?.Coordinates) {
            try {
              const coordinates = restaurant.location.Coordinates;

              // Coordinates are in [longitude, latitude] format
              const [longitude, latitude] = coordinates;

              // Get state from coordinates
              const stateFromCoordinates = getStateFromCoordinates(
                latitude,
                longitude
              );

              if (
                stateFromCoordinates !== "all" &&
                stateFromCoordinates === selectedState
              ) {
                return true;
              }
            } catch (error) {
              // Error processing restaurant coordinates - silently fail
            }
          }

          // Fallback to address-based filtering if no coordinates
          const address = restaurant.address?.toLowerCase() || "";
          return address.includes(selectedState.toLowerCase());
        });
      }

      // Apply date filter
      if (filter !== "all") {
        filtered = filterByDateRange(filter, filtered);
      }

      setFilteredData(filtered);
    }, 300); // Debounce delay

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [data, selectedState, filter]);

  // useEffect(() => {
  //   setTotalPages(Math.ceil(filteredData.length / itemsPerPage)); // Recalculate total pages whenever filtered data changes
  // }, [filteredData]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1); // Use functional updates
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1); // Use functional updates
    }
  };

  const downloadCSV = () => {
    if (filteredData.length === 0) return;

    const csvData = filteredData.map((restaurant, index) => ({
      "S/N": index + 1,
      Name: restaurant.name || "",
      Email: restaurant.email || "",
      Phone: restaurant.phone || "",
      Address: restaurant.address || "",
      Location:
        restaurant.location?.Coordinates?.length >= 2
          ? getStateFromCoordinates(
              restaurant.location.Coordinates[1],
              restaurant.location.Coordinates[0]
            )
          : "N/A",
      Status: restaurant.is_activated ? "Active" : "Inactive",
      Rating: restaurant.rating || 0,
      "Is Checked by Admin": restaurant.is_checked_by_admin ? "Yes" : "No",
      "Is Activated": restaurant.is_activated ? "Yes" : "No",
      "Registration Date": restaurant.created_at
        ? format(new Date(restaurant.created_at), "PPpp")
        : "N/A",
      "Last Login": restaurant.last_login
        ? format(new Date(restaurant.last_login), "PPpp")
        : "N/A",
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(","),
      ...csvData.map((row) => Object.values(row).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "restaurants_data.csv");
  };

  const filterByDateRange = (range: string, restaurants: Restaurant[]) => {
    const now = new Date();
    let filteredRestaurants = restaurants;

    if (range === "today") {
      const startOfDay = new Date(now.setHours(0, 0, 0, 0));
      filteredRestaurants = restaurants.filter((restaurant) => {
        if (!restaurant.last_login) return false;
        return new Date(restaurant.last_login) >= startOfDay;
      });
    } else if (range === "thisWeek") {
      const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
      filteredRestaurants = restaurants.filter((restaurant) => {
        if (!restaurant.last_login) return false;
        return new Date(restaurant.last_login) >= startOfWeek;
      });
    } else if (range === "thisMonth") {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      filteredRestaurants = restaurants.filter((restaurant) => {
        if (!restaurant.last_login) return false;
        return new Date(restaurant.last_login) >= startOfMonth;
      });
    } else if (range === "thisYear") {
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      filteredRestaurants = restaurants.filter((restaurant) => {
        if (!restaurant.last_login) return false;
        return new Date(restaurant.last_login) >= startOfYear;
      });
    }

    return filteredRestaurants;
  };

  if (isLoading) {
    return (
      <div className="h-[70vh] w-full bg-white flex flex-col items-center justify-center">
        <FoodLoader
          size="md"
          text="Fetching Restaurants"
          theme="amber"
          className="scale-75"
        />
      </div>
    );
  }

  return (
    <AppLayout>
      <div className="container mx-auto px-2 py-4">
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-2">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
              <div className="flex items-center gap-4">
                <BackButton />
                <h2 className="text-2xl font-bold text-amber-900">
                  Restaurant Management
                </h2>
              </div>
              <div className="w-full md:w-80">
                <SearchInput
                  search={handleSearch}
                  placeholder="Search by restaurant name..."
                />
              </div>
            </div>

            {/* Tab Switcher */}
            {/* <div className="flex space-x-4 mb-6">
              <div
                onClick={() => setTab(0)}
                className={`cursor-pointer py-2 px-6 rounded-md text-sm font-semibold ${
                  tab === 0
                    ? "bg-pr-primary text-white"
                    : "text-gray-500 hover:bg-gray-100"
                }`}
              >
                Restaurants
              </div>
            </div> */}

            {/* Tab Content */}
            {/* {tab === 0 && ( */}
            <>
              {isLoading ? (
                <div className="h-[70vh] w-full bg-white flex flex-col items-center justify-center">
                  <FoodLoader
                    size="md"
                    text="Fetching Restaurants"
                    theme="amber"
                    className="scale-75"
                  />
                </div>
              ) : error ? (
                <div className="text-red-500 py-10">
                  {(error as Error).message}
                </div>
              ) : (
                <div className="bg-amber-50 rounded-xl shadow-sm">
                  {/* Filter & Download Section */}
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 sm:p-6 border-b border-amber-200 gap-4">
                    <div className="flex items-center gap-3">
                      <label className="text-sm font-medium text-amber-900">
                        Filter by:
                      </label>
                      <div className="w-32">
                        <CustomSelect
                          value={filter}
                          onChange={(value) => setFilter(value as string)}
                          options={[
                            { label: "All", value: "all" },
                            { label: "Today", value: "today" },
                            { label: "This Week", value: "thisWeek" },
                            { label: "This Month", value: "thisMonth" },
                            { label: "This Year", value: "thisYear" },
                          ]}
                          placeholder="Filter"
                        />
                      </div>

                      <label className="text-sm font-medium text-amber-900">
                        State:
                      </label>
                      <div className="w-40">
                        <CustomSelect
                          value={selectedState}
                          onChange={(value) =>
                            setSelectedState(value as string)
                          }
                          options={NIGERIAN_STATES.map((state) => ({
                            label: state === "all" ? "All States" : state,
                            value: state,
                          }))}
                          placeholder="Select State"
                        />
                      </div>
                    </div>

                    {/* Download Button */}
                    <button
                      onClick={downloadCSV}
                      disabled={filteredData.length === 0}
                      className="flex items-center gap-2 px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-900 rounded-md border border-amber-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FontAwesomeIcon icon={faDownload} className="w-4 h-4" />
                      Download CSV
                    </button>
                  </div>

                  {/* Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm text-left">
                      <thead className="bg-amber-100/70 text-left text-xs font-semibold text-amber-900 uppercase tracking-wider">
                        <tr>
                          <th className="px-5 py-3 border-b border-amber-200 w-[60px]">
                            S/N
                          </th>
                          <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                            Name
                          </th>
                          <th className="px-5 py-3 border-b border-amber-200">
                            Email
                          </th>
                          <th className="px-5 py-3 border-b border-amber-200">
                            Status
                          </th>
                          <th className="px-5 py-3 border-b border-amber-200">
                            Address
                          </th>
                          <th className="px-5 py-3 border-b border-amber-200">
                            Location
                          </th>
                          <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                            Registration Date
                          </th>
                          <th className="px-5 py-3 border-b border-amber-200 w-[80px]">
                            View
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.length > 0 ? (
                          filteredData.map((restaurant, index) => {
                            const startIndex = (currentPage - 1) * itemsPerPage;
                            return (
                              <tr
                                key={restaurant._id}
                                className="hover:bg-amber-50/80"
                              >
                                <td className="px-5 py-3 border-b border-amber-100 font-medium">
                                  {startIndex + index + 1}
                                </td>
                                <td className="px-5 py-3 border-b border-amber-100">
                                  {restaurant.name}
                                </td>
                                <td className="px-5 py-3 border-b border-amber-100 max-w-[200px] truncate">
                                  {restaurant.email}
                                </td>
                                <td className="px-5 py-3 border-b border-amber-100">
                                  <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      restaurant.is_activated
                                        ? "bg-green-100 text-green-700"
                                        : "bg-red-100 text-red-700"
                                    }`}
                                  >
                                    {restaurant.is_activated
                                      ? "Active"
                                      : "Inactive"}
                                  </span>
                                </td>
                                <td className="px-5 py-3 border-b border-amber-100 max-w-[200px] truncate">
                                  {restaurant.address || "—"}
                                </td>
                                <td className="px-5 py-3 border-b border-amber-100">
                                  {restaurant.location?.Coordinates?.length >=
                                  2 ? (
                                    (() => {
                                      const location = getStateFromCoordinates(
                                        restaurant.location.Coordinates[1],
                                        restaurant.location.Coordinates[0]
                                      );
                                      const isExactMatch =
                                        !location.includes("km");

                                      return (
                                        <span
                                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                            isExactMatch
                                              ? "bg-amber-100 text-amber-800"
                                              : "bg-blue-100 text-blue-800"
                                          }`}
                                        >
                                          {location}
                                        </span>
                                      );
                                    })()
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </td>
                                <td className="px-5 py-3 border-b border-amber-200 max-w-[200px] truncate">
                                  {restaurant.created_at
                                    ? format(
                                        new Date(restaurant.created_at),
                                        "PPpp"
                                      )
                                    : "N/A"}
                                </td>
                                <td className="px-5 py-3 border-b border-amber-100">
                                  <a
                                    href={`/pr-admin/restaurants/restaurant-details/${restaurant._id}`}
                                  >
                                    <button
                                      className="p-1 rounded-full hover:bg-amber-100 transition-colors"
                                      aria-label="View restaurant details"
                                    >
                                      <img
                                        src={removeRedEye}
                                        alt="View"
                                        className="h-5 w-5"
                                      />
                                    </button>
                                  </a>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              colSpan={8}
                              className="text-center py-10 text-gray-500"
                            >
                              {searchTerm
                                ? "No restaurants found matching your search."
                                : selectedState !== "all"
                                ? `No restaurants found in ${selectedState}.`
                                : filter !== "all"
                                ? `No restaurants found for the selected filter (${filter}).`
                                : "No restaurants found."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="py-4 px-6 border-t border-amber-200">
                    <div className="flex items-center justify-between">
                      <button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        className={`px-4 py-2 rounded-md border border-amber-200 ${
                          currentPage === 1
                            ? "bg-amber-50 text-gray-400 cursor-not-allowed"
                            : "bg-amber-50 text-amber-900 hover:bg-amber-100"
                        }`}
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={handleNextPage}
                        disabled={filteredData.length < itemsPerPage}
                        className={`px-4 py-2 rounded-md border border-amber-200 ${
                          filteredData.length < itemsPerPage
                            ? "bg-amber-50 text-gray-400 cursor-not-allowed"
                            : "bg-amber-50 text-amber-900 hover:bg-amber-100"
                        }`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
            {/* )} */}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default RestaurantsManagement;
